import React, { useState, useEffect } from 'react';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, getDocs, collection, query, where, updateDoc, onSnapshot, arrayUnion } from '../../../lib/auditedFirestore';
import { db, firebaseConfig } from '../../../lib/firebase';
import Spinner from '../../../components/Spinner';

const AddUserForm = ({ roleToAdd, addedBy, setModal }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState(''); 
    const [password, setPassword] = useState('');
    const [canViewAuditLogs, setCanViewAuditLogs] = useState(false);
    
    // State for Sheets/Sessions & Papers
    const [sheets, setSheets] = useState([]);
    const [selectedSheet, setSelectedSheet] = useState('default');
    const [papers, setPapers] = useState([]);
    const [selectedPaper, setSelectedPaper] = useState('');
    
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const unsubscribeSheets = onSnapshot(query(collection(db, "sheets")), (snap) => {
            setSheets(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        const unsubscribePapers = onSnapshot(query(collection(db, "papers")), (snap) => {
            setPapers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubscribeSheets();
            unsubscribePapers();
        };
    }, []);

    const handleAddUser = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        const secondaryAppName = `secondary-app-${Date.now()}`;
        let secondaryApp;
        
        try {
            secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
            const secondaryAuth = getAuth(secondaryApp);
            const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
            const newUser = userCredential.user;
            
            const initialPapers = selectedPaper ? [selectedPaper] : [];

            await setDoc(doc(db, "users", newUser.uid), {
                email: newUser.email,
                name: name,
                phone: phone || null, 
                role: roleToAdd,
                createdAt: new Date(),
                createdBy: addedBy,
                sheetId: selectedSheet, // legacy fallback
                sheetIds: [selectedSheet], // New multi-session array
                assignedPapers: initialPapers,
                canViewAuditLogs: roleToAdd === 'admin' ? canViewAuditLogs : false
            });

            setModal({ show: true, message: `${roleToAdd.charAt(0).toUpperCase() + roleToAdd.slice(1)} added successfully!` });
            resetForm();
            await signOut(secondaryAuth);

        } catch (error) {
            // === MULTI-SESSION DUPLICATE HANDLING ===
            if (error.code === 'auth/email-already-in-use' && roleToAdd === 'student') {
                try {
                    const userQ = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
                    const querySnapshot = await getDocs(userQ);
                    
                    if (!querySnapshot.empty) {
                        const existingUserDoc = querySnapshot.docs[0];
                        const userRef = doc(db, 'users', existingUserDoc.id);
                        
                        // ADD TO NEW SESSION (Retains previous access)
                        await updateDoc(userRef, {
                            sheetId: selectedSheet, // Updates primary fallback
                            sheetIds: arrayUnion(selectedSheet), // Appends the new session
                            ...(phone && { phone: phone }), 
                            ...(selectedPaper && { assignedPapers: arrayUnion(selectedPaper) }) // Appends new paper
                        });
                        
                        setModal({ show: true, message: `Student already existed. Added to the new Session/Sheet and appended paper access.` });
                        resetForm();
                    }
                } catch (updateError) {
                    console.error("Error updating existing user:", updateError);
                    setModal({ show: true, message: `User exists, but failed to update access: ${updateError.message}` });
                }
            } else {
                console.error(`Error adding ${roleToAdd}:`, error);
                setModal({ show: true, message: `Error: ${error.message}` });
            }
        } finally {
            if (secondaryApp) {
                await deleteApp(secondaryApp).catch(err => console.error("Secondary app deletion failed", err));
            }
            setIsLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setEmail('');
        setPhone(''); 
        setPassword('');
        setCanViewAuditLogs(false);
        setSelectedPaper('');
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
            <h3 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Add New {roleToAdd.charAt(0).toUpperCase() + roleToAdd.slice(1)}</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`name-${roleToAdd}`}>Full Name</label>
                        <input id={`name-${roleToAdd}`} type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" required />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`email-${roleToAdd}`}>Email Address</label>
                        <input id={`email-${roleToAdd}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`${roleToAdd}@example.com`} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" required />
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`phone-${roleToAdd}`}>Phone Number</label>
                        <input id={`phone-${roleToAdd}`} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 1234567890" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor={`password-${roleToAdd}`}>Password</label>
                        <input id={`password-${roleToAdd}`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Set initial password" className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400" required />
                    </div>
                    
                    {roleToAdd === 'student' && (
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">Assign Initial Paper</label>
                            <select value={selectedPaper} onChange={(e) => setSelectedPaper(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
                                <option value="">-- None --</option>
                                {papers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {roleToAdd === 'student' && (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                        <label className="block text-purple-900 text-sm font-bold mb-2">Assign to Session / Sheet</label>
                        <select value={selectedSheet} onChange={(e) => setSelectedSheet(e.target.value)} className="w-full px-4 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white">
                            <option value="default">Default Session</option>
                            {sheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <p className="text-xs text-purple-600 mt-2">*If a student already exists, they will be duplicated into this new session alongside their old ones.</p>
                    </div>
                )}

                {roleToAdd === 'admin' && (
                    <div className="flex items-center p-3 bg-purple-50 rounded border border-purple-100 mt-2">
                        <input type="checkbox" id="auditLogsPermission" checked={canViewAuditLogs} onChange={(e) => setCanViewAuditLogs(e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer" />
                        <label htmlFor="auditLogsPermission" className="ml-2 block text-sm text-gray-800 font-medium cursor-pointer">
                            Grant Access to Audit Logs
                        </label>
                    </div>
                )}

                <button type="submit" disabled={isLoading} className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition disabled:from-purple-300 disabled:to-pink-300 flex items-center justify-center">
                    {isLoading ? <Spinner /> : `Add ${roleToAdd.charAt(0).toUpperCase() + roleToAdd.slice(1)}`}
                </button>
            </form>
        </div>
    );
};
export default AddUserForm;